import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../../components/TableBox';
import StatusBadge from '../../../../components/StatusBadge';
import RowActions from '../../../../components/RowActions';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import CustomizedSwitch from '../../../../components/Switch';
import useSorting from '../../../../hooks/useSorting';
import { sortData } from '../../../../utils/sortUtils';
import useFetch from '../../../../hooks/useFetch';
import usePagination from '../../../../hooks/usePagination';
import MasterService, { StatusMasterType } from '../../../../services/master.service';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { SortOrder, Modules, RouteParamsTypes } from '../../../../enums';

interface StatusRow {
  id: number;
  status_code: string;
  status_name: string;
  colour_code: string;
  is_active_state: boolean | null;
  display_order: number;
  notes: string | null;
}

interface StatusMasterListProps {
  type: StatusMasterType;
  routePath: string;
  hasActiveState: boolean;
}

const StatusMasterList: React.FC<StatusMasterListProps> = ({ type, routePath, hasActiveState }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'display_order' });
  const [items, setItems] = useState<StatusRow[]>([]);
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StatusRow | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const { loading } = useFetch({
    service: MasterService,
    func: 'list',
    payload: { type, includeDeleted: showInactive, page, limit: perPage },
    dependency: [type, showInactive, page, perPage, refreshToken],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${Modules.masters}${routePath}${RouteParamsTypes.edit}/${id}`);
    },
    [navigate, routePath]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const response: any = await MasterService.remove(type, deleteTarget.id);
      const softOrHard = response?.data?.softOrHard;
      toast.success(
        <Toast
          message={
            softOrHard === 'soft'
              ? t('masters.statuses.list.pageTexts.deleteSuccessSoft')
              : t('masters.statuses.list.pageTexts.deleteSuccessHard')
          }
        />,
        { icon: false }
      );
      setRefreshToken((v) => v + 1);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, type, t]);

  const headCells: HeadCellType[] = useMemo(() => {
    const cells: HeadCellType[] = [
      { id: 'status_code', sortEnabled: true, width: '15%', label: t('masters.statuses.list.pageTexts.table.columns.code') },
      { id: 'status_name', sortEnabled: true, width: '20%', label: t('masters.statuses.list.pageTexts.table.columns.name') },
      { id: 'colour_code', sortEnabled: true, width: '15%', label: t('masters.statuses.list.pageTexts.table.columns.colour') },
    ];
    if (hasActiveState) {
      cells.push({
        id: 'is_active_state',
        sortEnabled: true,
        width: '15%',
        label: t('masters.statuses.list.pageTexts.table.columns.isActiveState'),
      });
    }
    cells.push(
      { id: 'display_order', sortEnabled: true, width: '10%', label: t('masters.statuses.list.pageTexts.table.columns.displayOrder') },
      {
        id: 'actions',
        sortEnabled: false,
        width: '15%',
        label: t('masters.statuses.list.pageTexts.table.columns.actions'),
        textAlign: 'right',
      }
    );
    return cells;
  }, [t, hasActiveState]);

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: StatusRow; className?: string }) => (
      <tr className={className}>
        <td>{item.status_code}</td>
        <td>{item.status_name}</td>
        <td>
          <StatusBadge label={item.status_name} colour={item.colour_code} />
        </td>
        {hasActiveState && <td>{item.is_active_state ? <i className="ri-check-line text-success" /> : '—'}</td>}
        <td>{item.display_order}</td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'edit',
                label: t('common.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => handleEdit(item.id),
              },
              {
                id: 'delete',
                label: t('common.delete') || 'Delete',
                icon: <i className="ri-delete-bin-line" />,
                danger: true,
                onClick: () => setDeleteTarget(item),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [hasActiveState, handleEdit]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('masters.statuses.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('masters.statuses.list.pageTexts.title', { type: t(`masters.statuses.types.${type}`) })}</h4>
        <div className="d-flex align-items-center gap-4">
          <CustomizedSwitch
            label={t('masters.statuses.list.pageTexts.showInactive')}
            name="showInactive"
            checked={showInactive}
            onChange={() => setShowInactive((v) => !v)}
          />
          <Button
            className="btn btn-primary"
            text={t('masters.statuses.list.pageTexts.addNew')}
            onClick={() => navigate(`${Modules.masters}${routePath}${RouteParamsTypes.add}`)}
          />
        </div>
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('masters.statuses.list.pageTexts.deleteDialog.title')}
        content={t('masters.statuses.list.pageTexts.deleteDialog.body')}
        action={
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t('masters.statuses.list.pageTexts.deleteDialog.cancel')}
              onClick={() => setDeleteTarget(null)}
            />
            <Button
              className="btn btn-danger"
              text={t('masters.statuses.list.pageTexts.deleteDialog.confirm')}
              onClick={handleDeleteConfirm}
            />
          </>
        }
      />
    </>
  );
};

export default StatusMasterList;
